/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { Control } from "react-hook-form";
import { strings } from "../../../../common/Strings/Strings";
import { useUser } from "../../../../contexts/UserContext";
import CombinedSelect from "../../../../components/ReactHookFormFields/General/Select/CombinedSelect";
import { BasicClinicResponse } from "../../../../models/clinic/BasicClinicResponse";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal/Modal";
import { MapPin } from "../../../../common/Icons/MapPin";
import { Check } from "../../../../common/Icons/Check";
import { ClinicResponse } from "../../../../models/clinic/ClinicResponse";

interface ClinicLocation {
  countryCode: string;
  cities: string[];
  selectedCity?: string;
}

interface Props {
  clinics: BasicClinicResponse[];
  control: Control<any>;
  selectClinic(clinic: BasicClinicResponse): void;
}

const LocationFilter: React.FC<Props> = ({ clinics, control, selectClinic }: Props) => {
  const { user } = useUser();
  const [clinicLocations, setClinicLocations] = useState<Array<ClinicLocation>>([]);
  const [selectedClinicLocation, setSelectedClinicLocation] = useState<ClinicLocation>();

  const [isLoationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (clinics) {
      const citiesAndCountries: { [key: string]: Set<string> } = {};

      clinics.forEach(({ address }) => {
        if (address?.countryCode && citiesAndCountries[address?.countryCode]) {
          citiesAndCountries[address?.countryCode].add(address.city);
        } else if (address?.countryCode) {
          citiesAndCountries[address?.countryCode] = new Set([address.city]);
        }
      });

      const arr = Object.entries(citiesAndCountries).map(([countryCode, cities]) => ({
        countryCode,
        cities: [...cities],
      }));

      setClinicLocations(arr);
    }
  }, [clinics]);

  useEffect(() => {
    if (user.primaryAddress && clinicLocations?.length > 0) {
      setSelectedClinicLocation(clinicLocations.find((cl) => cl.countryCode === user.primaryAddress?.countryCode));
    }
  }, [user, clinicLocations]);

  return (
    <div>
      <div className="pb-4 flex flex-wrap">
        {strings.findClinic}{" "}
        <Button className=" ml-auto" onClick={() => setIsLocationModalOpen(true)} variant="link">
          <div className="flex">
            <MapPin variant="outline" /> {selectedClinicLocation?.countryCode}
            {selectedClinicLocation?.selectedCity ? ` | ${selectedClinicLocation.selectedCity}` : ""}
          </div>
        </Button>
      </div>
      <CombinedSelect
        name="selectedClinics"
        placeholder={strings.search}
        control={control}
        options={clinics.filter((clinic) => {
          if (selectedClinicLocation) {
            if (selectedClinicLocation.selectedCity) {
              return (
                clinic.address?.countryCode === selectedClinicLocation.countryCode &&
                clinic.address.city === selectedClinicLocation.selectedCity
              );
            } 
              return clinic.address?.countryCode === selectedClinicLocation.countryCode;
            
          } 
            return true;
          
        })}
        labelKey="name"
        label={strings.search}
        onChange={(selectedClinics: ClinicResponse[]) => {
          if (selectedClinics && selectedClinics[0]) {
            selectClinic(selectedClinics[0]);
          }
        }}
        allowNew={false}
      />
      <Modal handleClose={() => setIsLocationModalOpen(false)} size="modal-sm" show={isLoationModalOpen}>
        <Modal.Header title={strings.countryOrCity} />
        <Modal.Body>
          <div className="space-y-4">
            <div className="flex">
              <Button className="tw-link-dark" onClick={() => setSelectedClinicLocation(undefined)} variant="link">
                {strings.all}
              </Button>
              {selectedClinicLocation === undefined ? (
                <div className="ml-auto">
                  <Check />
                </div>
              ) : (
                <></>
              )}
            </div>
            {clinicLocations.map((clinicLocation) => (
              <div>
                <div className="flex">
                  <Button
                    className="tw-link-dark"
                    onClick={() => setSelectedClinicLocation({ ...clinicLocation, selectedCity: undefined })}
                    variant="link"
                  >
                    {strings[clinicLocation.countryCode] || clinicLocation.countryCode} ({clinicLocation.countryCode})
                  </Button>
                  {clinicLocation.countryCode === selectedClinicLocation?.countryCode ? (
                    <div className="ml-auto">
                      <Check />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                {clinicLocation.countryCode === selectedClinicLocation?.countryCode ? (
                  clinicLocation.cities.map((city) => (
                    <div className="flex pl-2">
                      <Button
                        className="tw-link-dark"
                        onClick={() => setSelectedClinicLocation({ ...clinicLocation, selectedCity: city })}
                        variant="link"
                      >
                        {city}
                      </Button>
                      {city === selectedClinicLocation?.selectedCity ? (
                        <div className="ml-auto">
                          <Check />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setIsLocationModalOpen(false)}>{strings.save}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LocationFilter;
